import { ActionIcon, Box, Collapse, Group, Stack, Tabs, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import { MouseEvent, ReactNode, useId, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { ScenarioScope } from '../scope';
import { ScenarioSummary } from '../types';
import { ArchivedModels } from './ArchivedModels';
import { groupModels } from './groups';
import { ModelList } from './ModelList';
import classes from './ModelList.module.css';
import { PrimaryModelCard } from './PrimaryModelCard';
import { useMemberFirstNames } from './useMemberFirstNames';

type ModelView = 'primary' | 'all' | 'archived';

type Props = {
  // Every model, archived ones included.
  scenarios: ScenarioSummary[];
  scope: ScenarioScope;
  // The section's own controls, at the right of the header.
  controls: ReactNode;
  // Shown in place of the tabs while there are no models at all.
  emptyState: ReactNode;
};

/**
 * The models section with the cashflow revamp: a divider like the tab's other sections,
 * closed until the adviser opens it. Inside, each group's primary model on its own by
 * default, so many models never crowd out the plans, and every model on the second tab.
 */
export function ModelViews({ scenarios, scope, controls, emptyState }: Props) {
  const [opened, { toggle }] = useDisclosure(false);
  const bodyId = useId();
  const [chosenView, setView] = useState<ModelView>('primary');
  const [focusedGroup, setFocusedGroup] = useState<string | null>(null);
  const names = useMemberFirstNames(scope);
  const active = scenarios.filter(scenario => !scenario.archived);
  const archived = scenarios.filter(scenario => scenario.archived);
  const groups = groupModels(active, scope);
  const archivedGroups = groupModels(archived, scope);
  const primaries = groups.filter(group => group.primary != null).length;
  // The Archived tab goes once its last model is restored, and takes the adviser back to the list.
  const view = chosenView === 'archived' && archived.length === 0 ? 'all' : chosenView;

  // The whole header opens and closes the divider, as the other sections' headers do, except
  // where the adviser clicked one of the tabs or controls that sit in it.
  const toggleFromHeader = (event: MouseEvent<HTMLElement>) => {
    if (!(event.target as HTMLElement).closest('button, a, [role="tab"]')) {
      toggle();
    }
  };

  const panels = (
    <>
      <Tabs.Panel value="primary">
        <Stack gap="md">
          {active.length === 0 && <NoActiveModels />}
          {groups.map(group => (
            <PrimaryModelCard
              key={group.key}
              group={group}
              names={names}
              scope={scope}
              onViewOthers={() => {
                setFocusedGroup(group.key);
                setView('all');
              }}
              onViewArchived={() => setView('archived')}
            />
          ))}
        </Stack>
      </Tabs.Panel>
      <Tabs.Panel value="all">
        {active.length === 0 && <NoActiveModels />}
        <ModelList
          groups={groups}
          names={names}
          scope={scope}
          focusedGroup={focusedGroup}
          onViewArchived={() => setView('archived')}
        />
      </Tabs.Panel>
      <Tabs.Panel value="archived">
        <ArchivedModels groups={archivedGroups} names={names} scope={scope} />
      </Tabs.Panel>
    </>
  );

  return (
    <Tabs
      value={view}
      onChange={value => {
        setFocusedGroup(null);
        setView(value === 'all' || value === 'archived' ? value : 'primary');
      }}
      color="brand"
      keepMounted={false}
      classNames={{ list: classes.tabList, tab: classes.tab }}
      className={`${classes.list} ${classes.divider}`}
      data-testid="cashflow-models-divider"
    >
      <Group className={classes.dividerHeader} gap="xl" wrap="nowrap" onClick={toggleFromHeader}>
        <UnstyledButton aria-expanded={opened} aria-controls={bodyId} onClick={toggle}>
          <Text fw={FONT_WEIGHTS.bold} size="lg" c="gray.10">
            Cashflow models
          </Text>
        </UnstyledButton>
        {opened && scenarios.length > 0 && (
          <Tabs.List>
            <Tabs.Tab value="primary">Primary models ({primaries})</Tabs.Tab>
            <Tabs.Tab value="all">All models ({active.length})</Tabs.Tab>
            {archived.length > 0 && (
              <Tabs.Tab value="archived">Archived ({archived.length})</Tabs.Tab>
            )}
          </Tabs.List>
        )}
        {!opened && (
          <Text size="sm" c="gray.6" data-testid="cashflow-models-count">
            {modelCount(active.length, archived.length)}
          </Text>
        )}
        <Group gap="xs" wrap="nowrap" ml="auto">
          {controls}
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={opened ? 'Close cashflow models' : 'Open cashflow models'}
            aria-expanded={opened}
            aria-controls={bodyId}
            onClick={toggle}
          >
            <IconChevronDown
              size={20}
              stroke={1.5}
              className={classes.dividerChevron}
              data-opened={opened || undefined}
            />
          </ActionIcon>
        </Group>
      </Group>
      <Collapse expanded={opened}>
        <Box id={bodyId} className={classes.dividerBody}>
          {scenarios.length === 0 ? emptyState : panels}
        </Box>
      </Collapse>
    </Tabs>
  );
}

// While the divider is closed, says what is inside it, so the adviser knows there is
// something to open. Open, the tabs carry the counts instead.
const modelCount = (active: number, archived: number): string => {
  if (active + archived === 0) {
    return 'No models yet';
  }
  const models = `${active} model${active === 1 ? '' : 's'}`;
  return archived > 0 ? `${models} · ${archived} archived` : models;
};

function NoActiveModels() {
  return (
    <Text size="sm" c="dimmed">
      Every model is archived. Restore one from the Archived tab, or build a new one.
    </Text>
  );
}
